import {
  REST_OF_REGION_LABS,
  type PowerLabId,
  type PowerRegionData,
  type PowerRegionId,
} from "../../../../power-bubble-chart/powerBubbleChartData"
import { type ComputeFragment } from "./powerTreemapLayout"
import {
  layoutComputeFragmentsStable,
  snapshotRowCache,
  restoreRowCache,
} from "./stableTreemap"

// ─────────────────────────────────────────────────────────────────────────────
// Target-state prune. The animation only looks clean if the TARGET each step has
// no sliver companies, so before handing a step to the layout we roll any
// company that would render under `minPx` on either side into its region's
// "Rest of X" block. It's iterative (rolling one in shrinks the rest and can
// push another under the threshold). Sizes are ESTIMATED with the REAL display
// layout (the persistent sticky-row treemap) run as a dry run — snapshotting and
// restoring its state so the estimate matches what will actually render without
// disturbing the animation.
// ─────────────────────────────────────────────────────────────────────────────

const REGION_ORDER: Record<string, number> = { america: 0, china: 1, world: 2 }

type WorkLab = { id: PowerLabId; name: string; regionId: PowerRegionId; borderOnly: boolean; compute: number }

// Build the final slot fragments (per region, size-rank slots) from the pruned
// working set — this is what the animation consumes.
const toSlotFragments = (regionOrder: PowerRegionId[], work: Map<PowerRegionId, WorkLab[]>): ComputeFragment[] => {
  const out: ComputeFragment[] = []
  for (const r of regionOrder) {
    const labs = [...work.get(r)!].sort((a, b) => b.compute - a.compute)
    labs.forEach((l, rank) => {
      out.push({
        id: `${r}-slot-${rank}`,
        lab: { id: l.id, name: l.name, regionId: l.regionId, compute: l.compute, borderOnly: l.borderOnly },
        value: Math.max(0, l.compute),
      })
    })
  }
  return out
}

// The rendered cell is inset by the inter-cell gap (see getCellBox): each
// interior side loses half the gap, edges lose none. Return the smaller rendered
// dimension so the prune tests what the reader actually sees, not the raw tile.
const renderedMinDim = (r: { x: number; y: number; width: number; height: number }, W: number, H: number, gapPx: number): number => {
  const hg = gapPx / 2
  const leftGap = r.x <= 0 ? 0 : hg
  const topGap = r.y <= 0 ? 0 : hg
  const rightGap = r.x + r.width >= W - 0.01 ? 0 : hg
  const bottomGap = r.y + r.height >= H - 0.01 ? 0 : hg
  return Math.min(r.width - leftGap - rightGap, r.height - topGap - bottomGap)
}

export const pruneSmallToRest = (
  regions: PowerRegionData[],
  includeNonFrontier: boolean,
  W: number,
  H: number,
  minPx: number,
  gapPx: number,
): ComputeFragment[] => {
  const regionOrder: PowerRegionId[] = []
  const work = new Map<PowerRegionId, WorkLab[]>()
  for (const region of regions) {
    const labs = region.labs
      .filter(l => l.compute > 0 && (includeNonFrontier || !l.borderOnly))
      .map(l => ({ id: l.id, name: l.name, regionId: region.id, borderOnly: !!l.borderOnly, compute: l.compute }))
    if (labs.length === 0) continue
    work.set(region.id, labs)
    regionOrder.push(region.id)
  }
  regionOrder.sort((a, b) => (REGION_ORDER[a] ?? 99) - (REGION_ORDER[b] ?? 99))
  if (regionOrder.length === 0 || W <= 0 || H <= 0) return toSlotFragments(regionOrder, work)

  // Estimate with the real sticky-row layout as a dry run; restore its state
  // afterwards so the animation's rows aren't disturbed.
  const snap = snapshotRowCache()
  try {
    for (let iter = 0; iter < 40; iter++) {
      // Each estimate is a DIRECT layout from the display's starting state to the
      // current set (matching what the display will actually do), not a stepwise
      // reconcile through the intermediate prunes.
      restoreRowCache(snap)
      const rects = layoutComputeFragmentsStable(toSlotFragments(regionOrder, work), 0, 0, W, H)
      const minDimByLab = new Map<PowerLabId, number>()
      for (const rc of rects) minDimByLab.set(rc.fragment.lab.id as PowerLabId, renderedMinDim(rc, W, H, gapPx))
      let prunedAny = false
      for (const r of regionOrder) {
        const restId = REST_OF_REGION_LABS[r].id
        const labs = work.get(r)!
        const keep: WorkLab[] = []
        let rest = labs.find(l => l.id === restId) ?? null
        let rolled = 0
        // 1. Roll every sub-8px company into the Rest block.
        for (const l of labs) {
          if (l.id === restId) continue
          const minDim = minDimByLab.get(l.id)
          if (minDim !== undefined && minDim < minPx) { rolled += l.compute; prunedAny = true }
          else keep.push(l)
        }
        if (rolled > 0) {
          if (!rest) rest = { id: restId, name: REST_OF_REGION_LABS[r].name, regionId: r, borderOnly: true, compute: 0 }
          rest = { ...rest, compute: rest.compute + rolled }
        }
        // 2. If the Rest block itself is a sliver (and no company was rolled this
        // pass, so its measured size is still valid), fold it INTO the smallest
        // remaining company. Direction matters: Rest here is by definition
        // sub-8px, so adding it to a company is a bounded, invisible size lie —
        // whereas absorbing the company into Rest swallowed it whole, however
        // large. (Plan B's merged national project, a region-dominant cell with
        // only sliver tails beside it, rendered as a border-only "Rest of US"
        // with no flag for 2032-33.)
        if (rest && rolled === 0 && keep.length > 0) {
          const restMin = minDimByLab.get(restId)
          if (restMin !== undefined && restMin < minPx) {
            let smallestIdx = 0
            for (let k = 1; k < keep.length; k++) if (keep[k].compute < keep[smallestIdx].compute) smallestIdx = k
            keep[smallestIdx] = {
              ...keep[smallestIdx],
              compute: keep[smallestIdx].compute + rest.compute,
            }
            rest = null
            prunedAny = true
          }
        }
        work.set(r, rest ? [...keep, rest] : keep)
      }
      if (!prunedAny) break
    }
  } finally {
    restoreRowCache(snap)
  }
  return toSlotFragments(regionOrder, work)
}
